/**
 * 日報/サマリ の「経費」画面(2026-09-10 指示で新設。社内ドラフト2案)。
 *  A案: 縦軸=日付 / 横軸=項目(現在の日報/サマリと同じ向き) ／ B案: 縦軸=項目 / 横軸=日付
 * 一覧は閲覧だけで、金額の入力は日付の「入力」から開くポップアップ(納品件数と同じ流れ。2026-09-10 指示)。
 * 項目は最終列(A案)・最終行(B案)の空欄に名前を入れると増える。つかんで並べ替え、削除は行の一番右。
 * CSV取込にも対応(1列目が日付＋項目名の列 ／ 日付・項目・金額 の3列)。
 * ※ PL の経費はこの入力値だけを使う(D-57)。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@core/auth/AuthProvider';
import { ChipSelect } from '@core/components/tableTools';
import { IconClose, IconGrip } from '@core/components/icons';
import { useToast } from '@core/components/Toast';
import { parseCsv, readCsvFile } from '@core/lib/csv';
import { todayStr, weekdayLabel } from '@core/lib/date';
import { dayTextClass } from '@core/lib/holidays';
import { formatYen } from '@core/lib/money';
import type { DateStr } from '@core/lib/types';
import { val } from './deliveryEdit';
import ExpenseDayModal from './ExpenseDayModal';
import { fetchExpenseDaily, fetchExpenseItems, newExpenseItemId, saveExpenses, type ExpenseItem } from './expenseRepo';

type Layout = 'A' | 'B';
const LAYOUT_LABELS: Record<Layout, string> = { A: 'A案: 縦=日付 / 横=項目', B: 'B案: 縦=項目 / 横=日付' };

/** 表示用の値: 日付 → 項目ID → 文字列 */
type Values = Record<DateStr, Record<string, string>>;

const nameStyle = { width: '100%', maxWidth: 132, padding: '2px 6px', border: '1px solid var(--line)', borderRadius: 6, fontFamily: 'inherit', fontSize: 12.5 } as const;

export default function ExpenseView({ dates, monthLabel, onDirtyChange }: { dates: DateStr[]; monthLabel: string; onDirtyChange: (dirty: boolean) => void }) {
  const { user, isAdmin } = useAuth();
  const toast = useToast();
  const [layout, setLayout] = useState<Layout>('A');
  const [items, setItems] = useState<ExpenseItem[]>([]);
  const [values, setValues] = useState<Values>({});
  const [savedItems, setSavedItems] = useState('[]');
  const [draftName, setDraftName] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dayModal, setDayModal] = useState<DateStr | null>(null);
  const [modalDirty, setModalDirty] = useState(false);
  /** 項目の編集(名前・並べ替え・削除)。ふだんは閉じて列を細くする(2026-09-10 指示: 横スクロールを減らす) */
  const [editItems, setEditItems] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [its, daily] = await Promise.all([fetchExpenseItems(), fetchExpenseDaily(dates[0]!, dates[dates.length - 1]!)]);
      const v: Values = {};
      for (const d of dates) {
        const row: Record<string, string> = {};
        for (const [k, n] of Object.entries(daily.get(d) ?? {})) row[k] = String(n);
        v[d] = row;
      }
      setItems(its);
      setValues(v);
      setSavedItems(JSON.stringify(its));
    } catch (e) {
      toast(e instanceof Error ? e.message : '経費の読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [dates, toast]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /** 項目マスタの未保存(金額はポップアップで即保存する) */
  const itemsDirty = JSON.stringify(items) !== savedItems;
  useEffect(() => {
    onDirtyChange(itemsDirty || modalDirty);
  }, [itemsDirty, modalDirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

  // ---- 項目の編集(追加・名前・削除・並べ替え) ----
  const addItem = (name: string) => {
    const n = name.trim();
    if (!n) return;
    setItems((prev) => [...prev, { id: newExpenseItemId(), name: n }]);
    setDraftName('');
  };
  const renameItem = (id: string, name: string) => setItems((prev) => prev.map((i) => (i.id === id ? { ...i, name } : i)));
  const removeItem = (id: string) => {
    const it = items.find((i) => i.id === id);
    if (it && !confirm(`「${it.name}」を削除します。入力済みの金額も次の保存で消えます。よろしいですか?`)) return;
    setItems((prev) => prev.filter((i) => i.id !== id));
  };
  const moveBefore = (srcId: string, dstId: string) =>
    setItems((prev) => {
      if (srcId === dstId) return prev;
      const from = prev.findIndex((x) => x.id === srcId);
      const to = prev.findIndex((x) => x.id === dstId);
      if (from < 0 || to < 0) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved!);
      return next;
    });
  const [dragId, setDragId] = useState<string | null>(null);
  const dragProps = (id: string) =>
    isAdmin && !busy
      ? {
          draggable: true,
          onDragStart: (e: React.DragEvent) => {
            setDragId(id);
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', id);
          },
          onDragEnd: () => setDragId(null),
          onDragOver: (e: React.DragEvent) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
          },
          onDrop: (e: React.DragEvent) => {
            e.preventDefault();
            const src = dragId ?? e.dataTransfer.getData('text/plain');
            if (src) moveBefore(src, id);
            setDragId(null);
          },
          className: dragId && dragId !== id ? 'drop-target' : undefined,
        }
      : {};

  async function saveItems() {
    if (!user) return;
    setBusy(true);
    try {
      await saveExpenses({ items, changed: [], userId: user.id });
      toast(`経費の項目を保存しました(${items.length}件)`);
      await reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  /**
   * CSV取込。次の2つの形に対応する:
   *  ・横型: 1列目が日付、2列目以降の見出しが項目名 ／ ・縦型: 日付 / 項目 / 金額 の3列
   */
  async function importCsv(file: File) {
    if (!user) return;
    setBusy(true);
    try {
      const table = parseCsv(await readCsvFile(file));
      const head = table.headers.map((h) => h.trim());
      const dateIdx = head.findIndex((h) => h.includes('日付') || h.toLowerCase() === 'date');
      if (dateIdx < 0) throw new Error('見出しに「日付」の列が必要です');
      const isLong = head.some((h) => h.includes('項目')) && head.some((h) => h.includes('金額'));
      const normDate = (s: string): DateStr | null => {
        const m = s.trim().replaceAll('/', '-').match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
        return m ? (`${m[1]}-${String(m[2]).padStart(2, '0')}-${String(m[3]).padStart(2, '0')}` as DateStr) : null;
      };
      const nextItems = [...items];
      const idOf = (name: string) => {
        const t = name.trim();
        const hit = nextItems.find((i) => i.name === t);
        if (hit) return hit.id;
        const added = { id: newExpenseItemId(), name: t };
        nextItems.push(added);
        return added.id;
      };
      const byDate = new Map<DateStr, Record<string, number>>();
      const outside = new Set<string>();
      const put = (date: DateStr, itemId: string, raw: string) => {
        if (!dates.includes(date)) {
          outside.add(date);
          return;
        }
        const n = val(raw);
        if (!Number.isInteger(n)) throw new Error(`${date} の金額「${raw}」が整数ではありません`);
        const cur = byDate.get(date) ?? { ...Object.fromEntries(Object.entries(values[date] ?? {}).map(([k, v]) => [k, val(v)])) };
        cur[itemId] = n;
        byDate.set(date, cur);
      };
      if (isLong) {
        const iItem = head.findIndex((h) => h.includes('項目'));
        const iAmt = head.findIndex((h) => h.includes('金額'));
        for (const r of table.rows) {
          const d = normDate(r[dateIdx] ?? '');
          const name = (r[iItem] ?? '').trim();
          if (!d || !name) continue;
          put(d, idOf(name), r[iAmt] ?? '0');
        }
      } else {
        const cols = head.map((h, i) => ({ i, name: h.trim() })).filter((c) => c.i !== dateIdx && c.name !== '' && !c.name.includes('合計'));
        for (const r of table.rows) {
          const d = normDate(r[dateIdx] ?? '');
          if (!d) continue;
          for (const c of cols) {
            const raw = (r[c.i] ?? '').trim();
            if (raw === '') continue;
            put(d, idOf(c.name), raw);
          }
        }
      }
      const changed = [...byDate.entries()].map(([date, v]) => ({ date, values: v }));
      if (changed.length === 0) throw new Error(`${monthLabel} に入る日付の行がありませんでした`);
      await saveExpenses({ items: nextItems, changed, userId: user.id });
      toast(`CSVから ${changed.length}日分を取り込みました(項目 ${nextItems.length}件)${outside.size > 0 ? ` ／ ${monthLabel} 以外の ${outside.size}日は飛ばしました` : ''}`);
      await reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'CSVを読めませんでした');
    } finally {
      setBusy(false);
    }
  }

  const dayTotal = (d: DateStr) => items.reduce((s, it) => s + val(values[d]?.[it.id] ?? ''), 0);
  const itemTotal = (id: string) => dates.reduce((s, d) => s + val(values[d]?.[id] ?? ''), 0);
  const grandTotal = useMemo(() => dates.reduce((s, d) => s + dayTotal(d), 0), [dates, values, items]); // eslint-disable-line react-hooks/exhaustive-deps
  const today = todayStr();
  const fmtDate = (d: DateStr) => `${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))}${weekdayLabel(d)}`;
  const amount = (n: number) => (n ? formatYen(n) : '');

  /** 項目名(つかんで並べ替え)。削除は A案=見出しの右端 / B案=行の一番右 */
  const itemCell = (it: ExpenseItem, withDelete: boolean) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      {isAdmin && (
        <span className="drag-handle" title="ドラッグして並べ替え">
          <IconGrip size={14} />
        </span>
      )}
      <input value={it.name} onChange={(e) => renameItem(it.id, e.target.value)} style={nameStyle} disabled={!isAdmin || busy} aria-label="項目名" />
      {withDelete && isAdmin && (
        <button type="button" className="txn-link" style={{ padding: '0 2px', color: 'var(--red)' }} title="この項目を削除" onClick={() => removeItem(it.id)}>
          <IconClose size={12} />
        </button>
      )}
    </div>
  );
  const newItemInput = (
    <input
      value={draftName}
      placeholder="＋ 新しい項目"
      onChange={(e) => setDraftName(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') addItem(draftName);
      }}
      onBlur={() => addItem(draftName)}
      style={nameStyle}
      disabled={!isAdmin || busy}
      aria-label="新しい項目"
    />
  );
  const inputBtn = (d: DateStr) => (
    <button type="button" className="btn-line" style={{ padding: '1px 10px', fontSize: 12 }} onClick={() => setDayModal(d)}>
      入力
    </button>
  );

  if (loading) return <div className="empty">読み込み中…</div>;

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 10 }}>
        <div className="tool-filter" style={{ width: '100%', rowGap: 6 }}>
          <ChipSelect
            label="表示案"
            options={(Object.keys(LAYOUT_LABELS) as Layout[]).map((l) => ({ key: l, label: LAYOUT_LABELS[l] }))}
            value={layout}
            onChange={(v) => setLayout(v === 'B' ? 'B' : 'A')}
            allLabel={null}
          />
          <span className="badge b-pend">社内ドラフト(2案)</span>
          <span className="hint">
            {monthLabel} 合計 ¥{formatYen(grandTotal)} ／ 項目 {items.length}件
          </span>
          {isAdmin && (
            <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8, alignItems: 'center' }}>
              {layout === 'A' && (
                <button className={`chip${editItems ? ' on' : ''}`} onClick={() => setEditItems((v) => !v)}>
                  {editItems ? '項目の編集中' : '項目を編集'}
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) void importCsv(f);
                }}
              />
              <button className="btn-line" style={{ padding: '4px 12px' }} disabled={busy} onClick={() => fileRef.current?.click()} title="1列目が日付、2列目以降が項目名のCSV(または 日付/項目/金額 の3列)">
                CSV取込
              </button>
              <button className="btn-green" style={{ padding: '5px 16px' }} disabled={busy || !itemsDirty} onClick={() => void saveItems()}>
                {busy ? '保存中…' : itemsDirty ? '項目を保存' : '項目の変更はありません'}
              </button>
            </span>
          )}
        </div>
      </div>

      <div className="table-scroll">
        {layout === 'A' ? (
          <table className="fit-table tree-table">
            <thead>
              <tr>
                <th style={{ minWidth: 88 }}>日付</th>
                {items.map((it) => (
                  <th key={it.id} className="num" style={{ minWidth: editItems ? 150 : 84 }} {...(editItems ? dragProps(it.id) : {})}>
                    {/* 編集中は見出しが入力欄になるので、スマホの縦積み用に項目名を文字でも置く */}
                    {editItems && <span className="sr-only">{it.name}</span>}
                    {editItems ? (
                      itemCell(it, true)
                    ) : (
                      <span title={it.name} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {it.name}
                      </span>
                    )}
                  </th>
                ))}
                {editItems && <th style={{ minWidth: 132 }}>{newItemInput}</th>}
                <th className="num" style={{ minWidth: 92, background: 'var(--legacy-shade)' }}>
                  合計
                </th>
                <th style={{ width: 58 }}></th>
              </tr>
            </thead>
            <tbody>
              {dates.map((d) => (
                <tr key={d} className={d === today ? 'is-today' : undefined}>
                  <td>
                    <span className={dayTextClass(d)}>{fmtDate(d)}</span>
                    {d === today && <span className="badge-today">今日</span>}
                  </td>
                  {items.map((it) => (
                    <td key={it.id} className="num">
                      {amount(val(values[d]?.[it.id] ?? ''))}
                    </td>
                  ))}
                  {editItems && <td />}
                  <td className="num" style={{ fontWeight: 600 }}>
                    {dayTotal(d) ? `¥${formatYen(dayTotal(d))}` : ''}
                  </td>
                  <td className="c no-clip">{isAdmin && inputBtn(d)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 700, background: 'var(--legacy-shade)' }}>
                <td>合計({monthLabel})</td>
                {items.map((it) => (
                  <td key={it.id} className="num">
                    {amount(itemTotal(it.id))}
                  </td>
                ))}
                {editItems && <td />}
                <td className="num">¥{formatYen(grandTotal)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        ) : (
          <table className="fit-table tree-table">
            <thead>
              <tr>
                <th style={{ minWidth: 190 }}>項目</th>
                {dates.map((d) => (
                  <th key={d} className={`num${d === today ? ' is-today-col' : ''}`} style={{ minWidth: 78 }}>
                    <span className={dayTextClass(d)}>{fmtDate(d)}</span>
                  </th>
                ))}
                <th className="num" style={{ minWidth: 96, background: 'var(--legacy-shade)' }}>
                  合計
                </th>
                <th style={{ width: 56 }}></th>
              </tr>
              {isAdmin && (
                <tr>
                  <th className="hint" style={{ fontWeight: 400 }}>
                    入力 →
                  </th>
                  {dates.map((d) => (
                    <th key={d} className="c no-clip">
                      {inputBtn(d)}
                    </th>
                  ))}
                  <th />
                  <th />
                </tr>
              )}
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} {...dragProps(it.id)}>
                  <td>{itemCell(it, false)}</td>
                  {dates.map((d) => (
                    <td key={d} className="num">
                      {amount(val(values[d]?.[it.id] ?? ''))}
                    </td>
                  ))}
                  <td className="num" style={{ fontWeight: 600 }}>
                    {itemTotal(it.id) ? `¥${formatYen(itemTotal(it.id))}` : ''}
                  </td>
                  <td className="c no-clip">
                    {isAdmin && (
                      <button type="button" className="txn-link" style={{ color: 'var(--red)' }} title="この項目を削除" onClick={() => removeItem(it.id)}>
                        削除
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              <tr>
                <td>{newItemInput}</td>
                {dates.map((d) => (
                  <td key={d} />
                ))}
                <td />
                <td />
              </tr>
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 700, background: 'var(--legacy-shade)' }}>
                <td>合計({monthLabel})</td>
                {dates.map((d) => (
                  <td key={d} className="num">
                    {amount(dayTotal(d))}
                  </td>
                ))}
                <td className="num">¥{formatYen(grandTotal)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        )}
      </div>
      <p className="hint" style={{ marginTop: 8 }}>
        金額は日付の「入力」から入れます。項目は{layout === 'A' ? '一番右の空欄' : '一番下の空欄'}に名前を入れると増え(Enter または他をクリックで確定)、項目名の左の印をつかんで並べ替え、
        {layout === 'A' ? '見出しの右の×印' : '行の一番右の「削除」'}で削除します(項目の変更は「項目を保存」で確定)。この金額は PL の経費に使われます。
      </p>
      {dayModal && (
        <ExpenseDayModal
          date={dayModal}
          items={items}
          initial={values[dayModal] ?? {}}
          onClose={() => setDayModal(null)}
          onSaved={() => void reload()}
          onDirtyChange={setModalDirty}
        />
      )}
    </>
  );
}
